import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '@/shared/lib/utils';
import { Icon, type PhxIconName } from '@/shared/ui/icon';

export type ButtonVariant =
  | 'primary'
  | 'default'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'link'
  | 'danger'
  | 'success'
  | 'info'
  | 'draft';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl' | 'icon' | 'icon-sm' | 'default';

// Pozn.: třídy se skládají přes pole + join, ne přes `'a ' + 'b'`. Konkatenace se
// spoléhá na mezeru na konci string literálu, kterou prettier-plugin-tailwindcss při
// řazení tříd může odstranit a tiše slepit dvě třídy dohromady (GUIDELINES ř. 161).
const VARIANT_CLASSES: Record<Exclude<ButtonVariant, 'secondary' | 'default'>, string> = {
  primary: [
    'bg-phx-primary text-phx-primary-fg border border-phx-primary',
    'hover:bg-phx-primary-hover hover:border-phx-primary-hover',
    'active:bg-phx-primary-active active:border-phx-primary-active',
  ].join(' '),
  outline: [
    'bg-transparent text-phx-primary border border-phx-primary',
    'hover:bg-phx-primary-tint',
  ].join(' '),
  // Hover musí být vidět i na plněném podkladu (hlavička/patička `Panel variant="filled"` je sama
  // `surface-2`), proto o stupeň tmavší výplň PLUS rámeček — ten odliší tlačítko na jakémkoliv pozadí.
  ghost: [
    'bg-transparent text-phx-text border border-transparent',
    'hover:bg-phx-surface-3 hover:border-phx-border-2',
    'active:bg-phx-surface-3 active:border-phx-border-3',
  ].join(' '),
  link: 'bg-transparent text-phx-link border-0 px-0 h-auto hover:underline',
  // Solidní varianty mění na hover/active barvu (ne `brightness`) – filtr ztmavuje i text a ikonu
  // a hlavně nepřebije `hover:bg-*` jiné varianty, pokud se varianty kombinují přes className.
  danger: [
    'bg-phx-danger text-phx-danger-fg border border-phx-danger',
    'hover:bg-phx-danger-hover hover:border-phx-danger-hover',
    'active:bg-phx-danger-active active:border-phx-danger-active',
  ].join(' '),
  success: [
    'bg-phx-success text-phx-success-fg border border-phx-success',
    'hover:bg-phx-success-hover hover:border-phx-success-hover',
    'active:bg-phx-success-active active:border-phx-success-active',
  ].join(' '),
  // Info a draft nemají dedikované hover/active barevné tokeny (na rozdíl od success/danger),
  // proto se na hover jen jemně zesvětlí přes opacity – konzistentní solid tón cílového stavu.
  info: ['bg-phx-info text-phx-info-fg border border-phx-info', 'hover:opacity-90'].join(' '),
  draft: ['bg-phx-draft text-phx-text-inv border border-phx-draft', 'hover:opacity-90'].join(' '),
};

const DEFAULT_VARIANT = [
  'bg-phx-surface text-phx-text border border-phx-border-2',
  'hover:bg-phx-surface-2 hover:border-phx-border-3',
  'active:bg-phx-surface-3',
].join(' ');

const SIZE_CLASSES: Record<Exclude<ButtonSize, 'default'>, string> = {
  sm: 'h-phx-control-sm px-phx-3 text-phx-xs gap-phx-2',
  md: 'h-phx-control px-phx-5 text-phx-md gap-phx-3',
  lg: 'h-phx-control-lg px-phx-5 text-phx-md gap-phx-3',
  xl: 'h-phx-control-xl px-phx-6 text-phx-lg gap-phx-3',
  icon: 'h-phx-control w-phx-control px-0',
  'icon-sm': 'h-phx-control-sm w-phx-control-sm px-0',
};

function resolveSize(size: ButtonSize | undefined): Exclude<ButtonSize, 'default'> {
  if (!size || size === 'default') return 'md';
  return size;
}

function resolveVariant(variant: ButtonVariant | undefined): {
  classes: string;
  isLink: boolean;
} {
  const value = variant ?? 'default';
  if (value === 'secondary' || value === 'default') {
    return { classes: DEFAULT_VARIANT, isLink: false };
  }
  return { classes: VARIANT_CLASSES[value], isLink: value === 'link' };
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: PhxIconName;
  iconRight?: PhxIconName;
  active?: boolean;
  loading?: boolean;
  asChild?: boolean;
}

const BASE_BUTTON = [
  'phx-btn inline-flex cursor-pointer items-center justify-center whitespace-nowrap select-none',
  'font-medium rounded-phx transition-colors',
  'focus-visible:outline-none focus-visible:shadow-phx-focus focus-visible:border-phx-primary',
  'disabled:opacity-50 disabled:cursor-not-allowed',
].join(' ');

/**
 * Řádkování NESMÍ být v BASE_BUTTON, ale až ZA velikostní třídou.
 * tailwind-merge má `font-size` ve skupině konfliktní s `leading` (nativní `text-sm` řádkování
 * taky nastavuje), takže `text-phx-*` ze SIZE_CLASSES by dřívější `leading-none` zahodilo —
 * a řádkování by spadlo na zděděných 1.45. U tlačítek s pevnou výškou se to neprojeví
 * (line-height se při `items-center` vykrátí), ale `variant="link"` má `h-auto` a narostl by.
 */
const LEADING = 'leading-none';

/**
 * Phoenix Button — compact, density-tuned button matching the Phoenix design
 * system (26px default height, 13px label, 4px radius).  Accepts the legacy
 * `secondary`/`default` variants for backwards compatibility with the
 * pre-design-system surface area.
 *
 * Built on: Phoenix design system (`phoenix-ui.jsx` + `phoenix-ui.css`).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = 'default',
    size,
    iconLeft,
    iconRight,
    active,
    loading,
    asChild,
    disabled,
    className,
    type,
    ...rest
  },
  ref
) {
  const resolvedSize = resolveSize(size);
  const { classes: variantClasses, isLink } = resolveVariant(variant);
  const isIconOnly = resolvedSize === 'icon' || resolvedSize === 'icon-sm';
  const iconSize = resolvedSize === 'sm' || resolvedSize === 'icon-sm' ? 12 : 14;
  const isDisabled = disabled || loading;

  // Bez `!` — `cn()` má phx barvy i škály zaregistrované v tailwind-merge, takže tenhle
  // override (je v cn() až za `variantClasses`) korektně přebije barvy varianty.
  const activeClass = active ? 'bg-phx-primary-tint border-phx-primary text-phx-primary' : '';

  const composed = cn(
    BASE_BUTTON,
    isLink ? '' : SIZE_CLASSES[resolvedSize],
    LEADING,
    variantClasses,
    activeClass,
    loading && 'cursor-wait',
    className
  );

  const content = (
    <>
      {loading && <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />}
      {!loading && iconLeft ? <Icon name={iconLeft} size={iconSize} /> : null}
      {children !== undefined && children !== null && children !== false ? (
        isIconOnly && !iconLeft && !iconRight ? (
          children
        ) : (
          <span>{children}</span>
        )
      ) : null}
      {!loading && iconRight ? <Icon name={iconRight} size={iconSize} /> : null}
    </>
  );

  if (asChild && isValidElement(children)) {
    const child = Children.only(children) as React.ReactElement<{
      className?: string;
      ref?: React.Ref<unknown>;
    }>;
    return cloneElement(child, {
      ...rest,
      className: cn(composed, child.props.className),
      ref,
    } as Record<string, unknown>);
  }

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={isDisabled}
      data-variant={variant}
      data-size={resolvedSize}
      data-active={active ? '' : undefined}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      className={composed}
      {...rest}
    >
      {content}
    </button>
  );
});
